'use client';

import { animateAngle, animateLngLat, calculateBearing, clampCoordinatesToBounds, gameCoordsToLatLng } from '@repo/types/maps/map-utils';
import * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePlayerLocation } from '../game-map/hooks/usePlayerLocation';
import { useMapContext } from '../game-map/map-context';
import { PlayerLocationMarkerGraphic } from './player-location-marker-graphic';
const MAX_ANIMATE_DISTANCE_M = 100_000;
export function PlayerLocationMarker({
  name,
  avatar
}: {
  name?: string;
  avatar?: string;
}) {
  const {
    map
  } = useMapContext();
  const {
    playerCoords,
    playerBearing,
    deltas
  } = usePlayerLocation();
  const markerRef = useRef<MapLibre.Marker | null>(null);
  const portalTargetRef = useRef<HTMLDivElement | null>(null);
  const [isOffscreen, setIsOffscreen] = useState(false);
  const [bearing, setBearing] = useState<number | null>(playerBearing);
  const [mapBearing, setMapBearing] = useState(0);
  const [offscreenBearing, setOffscreenBearing] = useState<number | null>(null);
  const bearingRef = useRef<number | null>(playerBearing);
  const lngLatRef = useRef<MapLibre.LngLat | null>(playerCoords && deltas ? gameCoordsToLatLng(playerCoords, deltas.deltas) : null);
  useEffect(() => {
    if (playerBearing === null) {
      bearingRef.current = null;
      setBearing(null);
      return;
    }
    const cancel = animateAngle({
      from: bearingRef.current ?? 0,
      to: playerBearing,
      duration: 300,
      onUpdate: value => {
        bearingRef.current = value;
        setBearing(value);
      }
    });
    return cancel;
  }, [playerBearing]);
  useEffect(() => {
    if (!map) return;
    const handleRotate = () => {
      const nextBearing = map.getBearing();
      setMapBearing(currentBearing => currentBearing === nextBearing ? currentBearing : nextBearing);
    };
    handleRotate();
    map.on('rotate', handleRotate);
    return () => {
      map.off('rotate', handleRotate);
    };
  }, [map]);
  useEffect(() => {
    if (!map) return;
    const el = document.createElement('div');
    portalTargetRef.current = el;
    markerRef.current = new MapLibre.Marker({
      element: el
    }).setLngLat([0, 0]).addTo(map);
    return () => {
      markerRef?.current?.remove();
      markerRef.current = null;
      portalTargetRef.current = null;
    };
  }, [map]);
  const updateMarker = useCallback(() => {
    const lngLat = lngLatRef.current;
    if (!markerRef.current || !map || !lngLat) return;
    const {
      clampedLngLat,
      isClamped
    } = clampCoordinatesToBounds(lngLat, map);
    setIsOffscreen(current => current === isClamped ? current : isClamped);
    markerRef.current.setLngLat([clampedLngLat.lng, clampedLngLat.lat]);
    const nextOffscreenBearing = isClamped ? calculateBearing(map.getCenter(), clampedLngLat) : null;
    setOffscreenBearing(current_0 => current_0 === nextOffscreenBearing ? current_0 : nextOffscreenBearing);
  }, [map]);
  useEffect(() => {
    if (!map) return;
    let resizeFrame: number | null = null;
    const handleResize = () => {
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        updateMarker();
      });
    };
    map.on('move', updateMarker);
    map.on('resize', handleResize);
    return () => {
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      map.off('move', updateMarker);
      map.off('resize', handleResize);
    };
  }, [map, updateMarker]);
  useEffect(() => {
    updateMarker();
  }, [updateMarker]);
  useEffect(() => {
    if (!playerCoords || !deltas) return;
    const playerLngLat = gameCoordsToLatLng(playerCoords, deltas.deltas);
    if (!playerLngLat) return;
    const cancel_0 = animateLngLat({
      from: lngLatRef.current ?? new MapLibre.LngLat(0, 0),
      to: playerLngLat,
      duration: 300,
      onUpdate: value_0 => {
        lngLatRef.current = value_0;
        updateMarker();
      },
      teleportThreshold: MAX_ANIMATE_DISTANCE_M
    });
    return cancel_0;
  }, [playerCoords, deltas, updateMarker]);
  return <>
            {portalTargetRef.current && ReactDOM.createPortal(<PlayerLocationMarkerGraphic bearing={bearing === null ? null : bearing - mapBearing} isOffscreen={isOffscreen} offscreenBearing={offscreenBearing === null ? null : offscreenBearing - mapBearing} name={name} avatar={avatar} />, portalTargetRef.current)}
        </>;
}